export const AUTH_JOURNEY_STAGE_STATES = [
  'pending',
  'active',
  'completed',
  'conditional-unresolved',
  'skipped',
  'blocked',
  'error',
] as const;

export type AuthJourneyStageState = (typeof AUTH_JOURNEY_STAGE_STATES)[number];

export type AuthJourneyOutcome =
  | 'in-progress'
  | 'completed'
  | 'exited'
  | 'failed';

export type AuthJourneyId =
  | 'sign-in'
  | 'registration'
  | 'workspace-setup'
  | 'workspace-invite'
  | 'workspace-invite-registration'
  | 'password-recovery'
  | 'magic-link'
  | 'oauth-sign-in'
  | 'terms-acceptance'
  | 'identity-action';

export type AuthJourneyTranslationKey = `com.streamlain.auth.journey.${string}`;

export interface AuthJourneyStageDefinition {
  id: string;
  labelKey: AuthJourneyTranslationKey;
  conditionLabelKey?: AuthJourneyTranslationKey;
  helpKey?: AuthJourneyTranslationKey;
  conditional?: boolean;
}

export interface AuthJourneyDefinition {
  id: AuthJourneyId;
  titleKey: AuthJourneyTranslationKey;
  goalKey: AuthJourneyTranslationKey;
  stages: readonly AuthJourneyStageDefinition[];
}

export interface AuthJourneyStageViewState {
  id: string;
  state: AuthJourneyStageState;
}

export interface AuthJourneyViewState {
  journeyId: AuthJourneyId;
  currentStageId: string | null;
  stages: readonly AuthJourneyStageViewState[];
  outcome: AuthJourneyOutcome;
}

const stage = (
  id: string,
  labelKey: AuthJourneyTranslationKey,
  options?: Omit<AuthJourneyStageDefinition, 'id' | 'labelKey'>
): AuthJourneyStageDefinition => ({ id, labelKey, ...options });

export const AUTH_JOURNEY_MANIFEST = {
  'sign-in': {
    id: 'sign-in',
    titleKey: 'com.streamlain.auth.journey.sign-in.title',
    goalKey: 'com.streamlain.auth.journey.sign-in.goal',
    stages: [
      stage('authenticate', 'com.streamlain.auth.journey.stage.authenticate'),
      stage('verify', 'com.streamlain.auth.journey.stage.verify', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.verify.condition',
      }),
      stage('terms', 'com.streamlain.auth.journey.stage.terms', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.terms.condition',
      }),
    ],
  },
  registration: {
    id: 'registration',
    titleKey: 'com.streamlain.auth.journey.registration.title',
    goalKey: 'com.streamlain.auth.journey.registration.goal',
    stages: [
      stage('account', 'com.streamlain.auth.journey.stage.account'),
      stage('verify', 'com.streamlain.auth.journey.stage.verify', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.verify.condition',
      }),
      stage(
        'secure-account',
        'com.streamlain.auth.journey.stage.secure-account',
        {
          conditional: true,
          conditionLabelKey:
            'com.streamlain.auth.journey.stage.secure-account.condition',
        }
      ),
      stage('workspace', 'com.streamlain.auth.journey.stage.workspace'),
    ],
  },
  'workspace-setup': {
    id: 'workspace-setup',
    titleKey: 'com.streamlain.auth.journey.workspace-setup.title',
    goalKey: 'com.streamlain.auth.journey.workspace-setup.goal',
    stages: [
      stage(
        'secure-account',
        'com.streamlain.auth.journey.stage.secure-account',
        {
          conditional: true,
          conditionLabelKey:
            'com.streamlain.auth.journey.stage.secure-account.condition',
        }
      ),
      stage('workspace', 'com.streamlain.auth.journey.stage.workspace'),
    ],
  },
  'workspace-invite': {
    id: 'workspace-invite',
    titleKey: 'com.streamlain.auth.journey.workspace-invite.title',
    goalKey: 'com.streamlain.auth.journey.workspace-invite.goal',
    stages: [
      stage('accept', 'com.streamlain.auth.journey.stage.accept-invite'),
    ],
  },
  'workspace-invite-registration': {
    id: 'workspace-invite-registration',
    titleKey: 'com.streamlain.auth.journey.workspace-invite.title',
    goalKey: 'com.streamlain.auth.journey.workspace-invite.goal',
    stages: [
      stage('invite', 'com.streamlain.auth.journey.stage.accept-invite'),
      stage('account', 'com.streamlain.auth.journey.stage.account'),
      stage('verify', 'com.streamlain.auth.journey.stage.verify'),
      stage(
        'secure-account',
        'com.streamlain.auth.journey.stage.secure-account'
      ),
    ],
  },
  'password-recovery': {
    id: 'password-recovery',
    titleKey: 'com.streamlain.auth.journey.password-recovery.title',
    goalKey: 'com.streamlain.auth.journey.password-recovery.goal',
    stages: [
      stage('request', 'com.streamlain.auth.journey.stage.request-link'),
      stage('reset', 'com.streamlain.auth.journey.stage.reset-password'),
    ],
  },
  'magic-link': {
    id: 'magic-link',
    titleKey: 'com.streamlain.auth.journey.magic-link.title',
    goalKey: 'com.streamlain.auth.journey.magic-link.goal',
    stages: [
      stage('verify', 'com.streamlain.auth.journey.stage.verify', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.verify.condition',
      }),
      stage('terms', 'com.streamlain.auth.journey.stage.terms', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.terms.condition',
      }),
    ],
  },
  'oauth-sign-in': {
    id: 'oauth-sign-in',
    titleKey: 'com.streamlain.auth.journey.oauth.title',
    goalKey: 'com.streamlain.auth.journey.oauth.goal',
    stages: [
      stage('verify', 'com.streamlain.auth.journey.stage.verify', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.verify.condition',
      }),
      stage('terms', 'com.streamlain.auth.journey.stage.terms', {
        conditional: true,
        conditionLabelKey: 'com.streamlain.auth.journey.stage.terms.condition',
      }),
    ],
  },
  'terms-acceptance': {
    id: 'terms-acceptance',
    titleKey: 'com.streamlain.auth.journey.terms.title',
    goalKey: 'com.streamlain.auth.journey.terms.goal',
    stages: [stage('terms', 'com.streamlain.auth.journey.stage.terms')],
  },
  'identity-action': {
    id: 'identity-action',
    titleKey: 'com.streamlain.auth.journey.identity-action.title',
    goalKey: 'com.streamlain.auth.journey.identity-action.goal',
    stages: [stage('change', 'com.streamlain.auth.journey.stage.change')],
  },
} as const satisfies Record<AuthJourneyId, AuthJourneyDefinition>;

export function createAuthJourneyViewState(
  journeyId: AuthJourneyId,
  currentStageId: string | null,
  stateByStage: Readonly<Record<string, AuthJourneyStageState>> = {},
  outcome: AuthJourneyOutcome = 'in-progress'
): AuthJourneyViewState {
  const definition = AUTH_JOURNEY_MANIFEST[journeyId];
  const stageIds = new Set(definition.stages.map(item => item.id));

  if (currentStageId !== null && !stageIds.has(currentStageId)) {
    throw new Error(
      `Unknown current stage "${currentStageId}" for journey "${journeyId}"`
    );
  }

  for (const id of Object.keys(stateByStage)) {
    if (!stageIds.has(id)) {
      throw new Error(`Unknown stage "${id}" for journey "${journeyId}"`);
    }
  }

  const stages = definition.stages.map(item => ({
    id: item.id,
    state:
      stateByStage[item.id] ??
      (item.id === currentStageId
        ? 'active'
        : item.conditional
          ? 'conditional-unresolved'
          : 'pending'),
  }));
  const current = stages.find(item => item.id === currentStageId);

  if (outcome === 'in-progress') {
    if (current && !['active', 'blocked', 'error'].includes(current.state)) {
      throw new Error(
        `Current stage in journey "${journeyId}" must be active, blocked, or error`
      );
    }
  } else if (currentStageId !== null) {
    throw new Error(
      `Terminal journey "${journeyId}" must not have a current stage`
    );
  }

  return { journeyId, currentStageId, stages, outcome };
}
